const waitForServiceWorkerWithTimeout = (timeoutMs = 5000): Promise<Awaited<ServiceWorkerRegistration>> => {
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Service worker ready timeout')), timeoutMs),
  );

  return Promise.race([navigator.serviceWorker.ready, timeoutPromise]);
};

const waitForServiceWorkerAck = (
  active: ServiceWorker,
  nonce: string,
  token: string,
  timeoutMs = 3000,
): Promise<void> => {
  const ackPromise = new Promise<void>((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => resolve();
    active.postMessage({ type: 'SET_DOWNLOAD_TOKEN', nonce, token }, [channel.port2]);
  });
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Service worker token ack timeout')), timeoutMs),
  );

  return Promise.race([ackPromise, timeoutPromise]);
};

const downloadWithServiceWorker = async (downloadURL: string, name: string, token?: string): Promise<void> => {
  try {
    const registration = await waitForServiceWorkerWithTimeout();
    const params = new URLSearchParams({ url: downloadURL, name });
    const active = registration.active;

    if (token && active) {
      const nonce = crypto.randomUUID();
      // Wait for SW to ack before navigating — eliminates race between message and fetch events
      await waitForServiceWorkerAck(active, nonce, token);
      params.set('nonce', nonce);
    }

    const link = document.createElement('a');
    link.href = `/sw-download/?${params.toString()}`;
    link.click();
  } catch {
    await downloadWithBlob(downloadURL, name, token);
  }
};

const downloadWithBlob = async (downloadURL: string, name: string, token?: string): Promise<void> => {
  const headers: HeadersInit = token ? { authorization: `Bearer ${token}` } : {};
  const response = await fetch(downloadURL, { headers });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const blob = await response.blob();

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();

  URL.revokeObjectURL(link.href);
};

export const download = async (downloadURL: string, name: string, token?: string): Promise<void> => {
  if ('serviceWorker' in navigator) {
    await downloadWithServiceWorker(downloadURL, name, token);
  } else {
    await downloadWithBlob(downloadURL, name, token);
  }
};
